import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet } from 'react-native';
import { H2, H5, Spinner, YStack, XStack, Separator, Button as TamaguiButton, Text as TamaguiText } from 'tamagui';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import ModernStageCard from './ModernStageCard';
import ModernAddStage from './ModernAddStage';
import AnimatedLottieView from '@utils/lottieAnimation';
import { StageSchema } from '../ActiveCampaigns';
import apis from '@api/api';
import * as Animatable from 'react-native-animatable';
import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialIcons, AntDesign } from '@expo/vector-icons';
import { View, Text } from '@constants/Themed';
import { Stepper, Step, StepLabel, Button as MuiButton, Box } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import GenerateSchemaForm from '@hooks/generateSchemaForm';

interface CampaignStageBuilderProps {
  closeLoading?: Function;
}

const steps = ['Build Stages', 'Configure Details', 'Review & Finish'];

const CampaignStageBuilder = forwardRef<any, CampaignStageBuilderProps>((props, ref) => {
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const theme = themeSelector();
  const [stages, setStages] = useState<StageSchema[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedStage, setExpandedStage] = useState<number | null>(null);
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);

  const muiTheme = createTheme({
    palette: {
      mode: theme ? 'light' : 'dark',
      primary: {
        main: theme ? '#3b82f6' : '#2563eb',
      },
      background: {
        default: theme ? '#f8fafc' : '#0f172a',
        paper: theme ? '#ffffff' : '#1e293b',
      },
      text: {
        primary: theme ? '#1e293b' : '#f1f5f9',
        secondary: theme ? '#64748b' : '#94a3b8',
      },
    },
    components: {
      MuiStepLabel: {
        styleOverrides: {
          label: {
            color: theme ? '#64748b' : '#94a3b8',
            '&.Mui-active': {
              color: theme ? '#1e293b' : '#f1f5f9',
              fontWeight: 'bold',
            },
            '&.Mui-completed': {
              color: theme ? '#10b981' : '#34d399',
            },
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: theme ? '#e2e8f0' : '#334155',
            '&.Mui-active': {
              color: theme ? '#3b82f6' : '#2563eb',
            },
            '&.Mui-completed': {
              color: theme ? '#10b981' : '#34d399',
            },
          },
        },
      },
    },
  });

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useImperativeHandle(ref, () => ({
      retrieveValues: () => {
        return stages;
      }
    }));

  const _useDefaultStages = async () => {
    setLoading(true);
    try {
      let correctedSalesTeam: any[] = [];

      // Try to get team if company is available
      if (currentCompany?.id) {
        const team = await apis.getTeam(currentCompany.id).catch(err => {
          console.log('Could not fetch team:', err);
          return [];
        });
        const salesTeam = team.flatMap(user => user.user);

        correctedSalesTeam = salesTeam.map((user) => ({
          ...user,
          value: user?._id,
          name: user.profile_information.name,
          avatar: user.profile_information.avatar.img,
          surname: user.profile_information.surname,
        }));
      }

      const getRandomUser = () => {
        if (correctedSalesTeam.length === 0) return undefined;
        const random = Math.floor(Math.random() * correctedSalesTeam.length);
        return correctedSalesTeam[random];
      }

      setStages([
        {
          title: 'Prospecting',
          description: 'Identify and research potential leads',
          owner: getRandomUser(),
          category: 'Discovery',
          headline: 'Initial Contact'
        },
        {
          title: 'Qualifying',
          description: 'Evaluate lead quality and fit',
          owner: getRandomUser(),
          category: 'Assessment',
          headline: 'Needs Analysis'
        },
        {
          title: 'Proposal',
          description: 'Present solution and value proposition',
          owner: getRandomUser(),
          category: 'Presentation',
          headline: 'Solution Pitch'
        },
        {
          title: 'Negotiation',
          description: 'Address concerns and finalize terms',
          owner: getRandomUser(),
          category: 'Closing',
          headline: 'Deal Discussion'
        },
        {
          title: 'Closed Won',
          description: 'Successfully completed deals',
          owner: getRandomUser(),
          category: 'Completion',
          headline: 'Victory!'
        },
      ]);
      setLoading(false);
      } catch (err) {
        console.log(err);
        setLoading(false);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: "An error occurred while trying to fetch the team. Please try again later.",
          type: "error"
        }
      });
      }
    }

  const _useAIStages = async () => {
    setLoading(true);
    try {
      let correctedSalesTeam: any[] = [];

      // Try to get team if company is available
      if (currentCompany?.id) {
        const team = await apis.getTeam(currentCompany.id).catch(err => {
          console.log('Could not fetch team:', err);
          return [];
        });
      const salesTeam = team.flatMap(user => user.user);

        correctedSalesTeam = salesTeam.map((user) => ({
          ...user,
          value: user?._id,
          name: user.profile_information?.name,
          avatar: user.profile_information?.avatar?.img,
          surname: user.profile_information?.surname,
        }));
      }

      // Call AI to generate optimal stages
      const response = await apis.noMonitor.post('/ai/campaign/suggest-stages', {
        company_id: currentCompany?.id || null,
        team: correctedSalesTeam,
        campaign_type: 'sales',
        context: {
          industry: currentCompany?.industry_type || 'General',
          team_size: correctedSalesTeam.length
        }
      });

      if (response.data.success && response.data.stages) {
        const aiStages = response.data.stages.map((stage: any, index: number) => ({
          title: stage.title,
          description: stage.description,
          owner: correctedSalesTeam.length > 0 ? correctedSalesTeam[index % correctedSalesTeam.length] : undefined,
          category: stage.category || 'General',
          headline: stage.headline || stage.title
        }));

        setStages(aiStages);
      } else {
        console.warn('AI stage generation returned no stages, using defaults');
        await _useDefaultStages();
      }
      setLoading(false);
    } catch (err) {
      console.error('AI stage generation failed:', err);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: "AI stage generation failed. Using default stages.",
          type: "warning"
        }
      });
      await _useDefaultStages();
    }
  }

  const _addStage = useCallback((values: StageSchema) => {
      setStages([...stages, values]);
    setExpandedStage(null);
  }, [stages]);

  const _updateStage = useCallback((index: number, values: StageSchema) => {
    const updatedStages = [...stages];
    updatedStages[index] = { ...updatedStages[index], ...values };
    setStages(updatedStages);
    setExpandedStage(null);
  }, [stages]);

  const _deleteStage = useCallback((index: number) => {
    const newStages = stages.filter((_, i) => i !== index);
      setStages(newStages);
    setExpandedStage(null);
  }, [stages]);

  const _moveStage = useCallback((fromIndex: number, toIndex: number) => {
    const newStages = [...stages];
    const [movedStage] = newStages.splice(fromIndex, 1);
    newStages.splice(toIndex, 0, movedStage);
    setStages(newStages);
  }, [stages]);

  const _clearAllStages = () => {
    setStages([]);
    setExpandedStage(null);
  }

  const stageColors = [
    { primary: theme ? "#3b82f6" : "#2563eb", secondary: theme ? "#dbeafe" : "#1e3a8a" },
    { primary: theme ? "#10b981" : "#059669", secondary: theme ? "#d1fae5" : "#064e3b" },
    { primary: theme ? "#f59e0b" : "#d97706", secondary: theme ? "#fef3c7" : "#78350f" },
    { primary: theme ? "#8b5cf6" : "#7c3aed", secondary: theme ? "#ede9fe" : "#4c1d95" },
    { primary: theme ? "#ec4899" : "#db2777", secondary: theme ? "#fce7f3" : "#831843" },
  ];

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              paddingHorizontal: 24,
              paddingVertical: 20,
              gap: 16,
              flexGrow: 1
            }}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
          >
            {loading ? (
              <Animatable.View
                animation="fadeIn"
                style={[
                  modernStyles.loadingContainer,
                  { backgroundColor: theme ? "#ffffff" : "#1e293b" }
                ]}
              >
                <AnimatedLottieView
                  source={require('@assets/animations/spin-leaf-loader.json')}
                  style={{ height: 120, width: 120 }}
                  autoPlay
                  loop
                />
                <H5 style={{
                  color: theme ? "#64748b" : "#94a3b8",
                  marginTop: 16
                }}>
                  Generating stages...
                </H5>
              </Animatable.View>
            ) : stages.length === 0 ? (
              // Empty State
              <Animatable.View
                animation="fadeIn"
                style={[
                  modernStyles.emptyState,
                  { backgroundColor: theme ? "#ffffff" : "#1e293b" }
                ]}
              >
                <View style={{
                  backgroundColor: theme ? "#ddd6fe" : "#4c1d95",
                  borderRadius: 20,
                  padding: 20,
                  marginBottom: 20
                }}>
                  <MaterialIcons
                    name="auto-awesome"
                    size={48}
                    color={theme ? "#7c3aed" : "#a78bfa"}
                  />
                </View>

                <H2 style={{
                  color: theme ? "#1e293b" : "#f1f5f9",
                  fontSize: 24,
                  fontWeight: '700',
                  marginBottom: 8,
                  textAlign: 'center'
                }}>
                  Create Your Pipeline
                </H2>

                <TamaguiText style={{
                  color: theme ? "#64748b" : "#94a3b8",
                  fontSize: 16,
                  textAlign: 'center',
                  marginBottom: 32,
                  lineHeight: 24,
                  paddingHorizontal: 20
                }}>
                  Generate stages automatically with AI or use proven default templates.
                  AI creates optimal stages based on your industry and team.
                </TamaguiText>

                <YStack gap={12} width="100%" maxWidth={400}>
                  <TamaguiButton
                    onPress={_useAIStages}
                    size="$5"
                    style={{
                      backgroundColor: theme ? "#7c3aed" : "#6d28d9",
                      borderWidth: 0,
                      shadowColor: "#7c3aed",
                      shadowOpacity: 0.3,
                      shadowRadius: 12,
                      elevation: 6
                    }}
                    icon={<MaterialIcons name="auto-awesome" size={20} color="white" />}
                  >
                    <TamaguiText style={{
                      color: 'white',
                      fontSize: 16,
                      fontWeight: '600'
                    }}>
                      AI Generate Stages
                    </TamaguiText>
                  </TamaguiButton>

                  <TamaguiButton
                    onPress={_useDefaultStages}
                    size="$5"
                    chromeless
                    style={{
                      borderWidth: 2,
                      borderColor: theme ? "#7c3aed" : "#6d28d9",
                      backgroundColor: theme ? "#faf5ff" : "#2e1065"
                    }}
                    icon={<MaterialIcons name="dashboard" size={20} color={theme ? "#7c3aed" : "#a78bfa"} />}
                  >
                    <TamaguiText style={{
                      color: theme ? "#7c3aed" : "#a78bfa",
                      fontSize: 16,
                      fontWeight: '600'
                    }}>
                      Use Default Stages
                    </TamaguiText>
                  </TamaguiButton>
                </YStack>
              </Animatable.View>
            ) : (
              // Stages List
              <YStack gap={16}>
                {stages.map((stage, index) => (
                  <ModernStageCard
                    key={index}
                    stage={stage}
                    index={index}
                    totalStages={stages.length}
                    isExpanded={expandedStage === index}
                    onToggleExpand={() => setExpandedStage(expandedStage === index ? null : index)}
                    onUpdate={(values) => _updateStage(index, values)}
                    onDelete={() => _deleteStage(index)}
                    onMoveUp={index > 0 ? () => _moveStage(index, index - 1) : undefined}
                    onMoveDown={index < stages.length - 1 ? () => _moveStage(index, index + 1) : undefined}
                    colors={stageColors[index % stageColors.length]}
                    theme={theme}
                  />
                ))}

                {/* Add New Stage Button */}
                <ModernAddStage
                  onAdd={_addStage}
                  stageNumber={stages.length + 1}
                  theme={theme}
                />
              </YStack>
            )}
          </ScrollView>
        );
      case 1:
        return (
          <YStack flex={1} gap="$4" padding="$4">
            <GenerateSchemaForm
              ref={formRef}
              schema={[
                { type: 'string', title: 'Title', required: true },
                { type: 'string', title: 'Description' },
                { type: 'string', title: 'Category' },
                { type: 'string', title: 'Headline' },
                { type:'customer_datas', title: 'Dataset Selection', ref: 'customer_datas' },
              ]}
              hideButton
            />
          </YStack>
        );
      case 2:
        return (
          <YStack flex={1} justifyContent="center" alignItems="center" gap="$4">
            <H2>Review & Finish</H2>
            <TamaguiText>Review your campaign stages before finishing.</TamaguiText>
          </YStack>
        );
      default:
        return null;
    }
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <View style={[
        modernStyles.container,
        { backgroundColor: theme ? "#f8fafc" : "#0f172a", flexGrow: 1, minHeight: "100%", paddingHorizontal: "2%" }
      ]}>
        <Box padding={0} sx={{ width: '100%', maxHeight: 100, padding: 20 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        {/* Header */}
        <XStack
          style={{
            paddingHorizontal: 24,
            paddingVertical: 20,
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottomWidth: 1,
            borderBottomColor: theme ? "#e2e8f0" : "#334155"
          }}
        >
          <XStack gap={12} alignItems="center">
            <View style={{
              backgroundColor: theme ? "#ddd6fe" : "#4c1d95",
              borderRadius: 12,
              padding: 10
            }}>
              <MaterialIcons
                name="timeline"
                size={24}
                color={theme ? "#7c3aed" : "#a78bfa"}
              />
            </View>
            <View style={{ backgroundColor: "transparent" }}>
              <H2 style={{
                color: theme ? "#1e293b" : "#f1f5f9",
                fontSize: 24,
                fontWeight: '700'
              }}>
                Campaign Stages
              </H2>
              <TamaguiText style={{
                color: theme ? "#64748b" : "#94a3b8",
                fontSize: 14
              }}>
                {stages.length > 0 ? `${stages.length} stage${stages.length !== 1 ? 's' : ''} configured` : 'Build your campaign pipeline'}
              </TamaguiText>
            </View>
          </XStack>

          {stages.length > 0 && (
            <TamaguiButton
              onPress={_clearAllStages}
              size="$3"
              chromeless
              icon={<AntDesign name="delete" size={18} color={theme ? "#ef4444" : "#f87171"} />}
              style={{
                borderWidth: 1.5,
                borderColor: theme ? "#fecaca" : "#7f1d1d",
                backgroundColor: theme ? "#fef2f2" : "#450a0a"
              }}
            >
              <TamaguiText style={{
                color: theme ? "#ef4444" : "#f87171",
                fontWeight: '600'
              }}>
                Clear All
              </TamaguiText>
            </TamaguiButton>
          )}
        </XStack>

        {/* Content - Constrained Scroll Area */}
        <View style={{ flex: 1, minHeight: 0 }}>
          {renderStepContent(activeStep)}
      </View>
        <Box sx={{ display: 'flex', flexDirection: 'row', p: 2, borderTop: 1, borderColor: 'divider' }}>
          <MuiButton
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1, color: theme ? '#64748b' : '#94a3b8' }}
          >
            Back
          </MuiButton>
          <Box sx={{ flex: '1 1 auto' }} />
          <MuiButton onClick={handleNext} variant="contained">
            {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
          </MuiButton>
        </Box>
    </View>
    </ThemeProvider>
  );
});

const modernStyles = StyleSheet.create({
  container: {
    flex: 1,
    maxHeight: 700,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
    borderRadius: 16,
    minHeight: 400,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 6,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    borderRadius: 16,
    minHeight: 500,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 6,
  }
});

export default CampaignStageBuilder;
